import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Users, Coins, Euro, FileText, Boxes, AlertTriangle, Wallet,
    Activity, ShoppingBag, Rss, Building2, Award, Microscope, Send,
} from "lucide-react";
import { api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";

function KpiCard({ label, value, hint, icon: Icon, tone = "default", testid }) {
    const toneClass = {
        default: "border-[var(--color-border)]",
        good: "border-[var(--color-success)]/40 bg-[var(--color-success)]/5",
        warn: "border-[var(--color-alert)]/40 bg-[var(--color-alert)]/5",
        primary: "border-[var(--color-primary)]/40 bg-[var(--color-primary)]/5",
    }[tone];
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid={testid}
            className={`rounded-2xl border bg-[var(--color-card)] p-5 ${toneClass}`}
        >
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-widest text-text-secondary">{label}</span>
                {Icon && <Icon className="h-4 w-4 text-text-secondary" />}
            </div>
            <div className="text-2xl font-semibold tabular-nums">{value}</div>
            {hint && <p className="text-xs text-text-secondary mt-1">{hint}</p>}
        </motion.div>
    );
}

const PLAN_ORDER = ["free", "starter", "pro", "business", "enterprise"];

export default function AdminDashboard() {
    const [collapsed, setCollapsed] = useState(false);
    const [kpi, setKpi] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const data = await api.adminDashboardKpi();
                setKpi(data);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="flex min-h-screen bg-[var(--color-bg)] text-text-main">
                <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
                <div className="flex-1 flex flex-col min-w-0">
                    <Header />
                    <main className="p-10 text-text-secondary">…</main>
                </div>
            </div>
        );
    }
    if (error) {
        return (
            <div className="flex min-h-screen bg-[var(--color-bg)] text-text-main">
                <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
                <div className="flex-1 flex flex-col min-w-0">
                    <Header />
                    <main className="p-10 text-[var(--color-alert)]">Error: {error}</main>
                </div>
            </div>
        );
    }

    const planMap = Object.fromEntries((kpi.plan_distribution || []).map((r) => [r.plan_id, r.count]));

    return (
        <div className="flex min-h-screen bg-[var(--color-bg)] text-text-main">
            <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="p-6 sm:p-10 max-w-7xl w-full">
                    <div className="mb-8">
                        <p className="text-[10px] uppercase tracking-widest text-text-secondary">Phase 6A · Unified admin</p>
                        <h1 className="text-3xl font-semibold tracking-tight mt-1">Admin dashboard</h1>
                        <p className="text-sm text-text-secondary mt-1">
                            Live aggregates across users, revenue, blog, marketplace and economy.
                        </p>
                    </div>

                    {/* Top KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
                        <KpiCard
                            testid="admin-kpi-users-total"
                            label="Total users" value={kpi.users?.total ?? 0}
                            hint={`Active last 7d: ${kpi.users?.active_7d ?? 0}`}
                            icon={Users}
                        />
                        <KpiCard
                            testid="admin-kpi-credits-month"
                            label="Credits sold (month)"
                            value={(kpi.credits?.sold_month ?? 0).toLocaleString()}
                            hint={`Total: ${(kpi.credits?.sold_total ?? 0).toLocaleString()}`}
                            icon={Coins}
                        />
                        <KpiCard
                            testid="admin-kpi-revenue-month"
                            label="Revenue (month)"
                            value={`$${(kpi.revenue_eur?.month ?? 0).toFixed(2)}`}
                            hint={`Total: $${(kpi.revenue_eur?.total ?? 0).toFixed(2)}`}
                            icon={Euro}
                            tone="primary"
                        />
                        <KpiCard
                            testid="admin-kpi-blog"
                            label="Blog"
                            value={`${kpi.blog?.published ?? 0}`}
                            hint={`Published — ${kpi.blog?.draft ?? 0} drafts`}
                            icon={Rss}
                        />
                        <KpiCard
                            testid="admin-kpi-marketplace"
                            label="Marketplace purchases (month)"
                            value={kpi.marketplace_purchases_month ?? 0}
                            icon={ShoppingBag}
                        />
                        <KpiCard
                            testid="admin-kpi-suspended"
                            label="Suspended users"
                            value={kpi.suspended_users ?? 0}
                            hint={
                                (kpi.suspended_users ?? 0) > 0
                                    ? <Link to="/admin/economy" className="text-[var(--color-alert)]">Review →</Link>
                                    : "All good"
                            }
                            icon={AlertTriangle}
                            tone={(kpi.suspended_users ?? 0) > 0 ? "warn" : "default"}
                        />
                        <KpiCard
                            testid="admin-kpi-payouts"
                            label="Pending affiliate payouts"
                            value={kpi.pending_affiliate_payouts ?? 0}
                            hint={
                                (kpi.pending_affiliate_payouts ?? 0) > 0
                                    ? <Link to="/admin/affiliate-payouts" className="text-[var(--color-secondary)]">Process →</Link>
                                    : "None due"
                            }
                            icon={Wallet}
                            tone={(kpi.pending_affiliate_payouts ?? 0) > 0 ? "primary" : "default"}
                        />
                        <KpiCard
                            testid="admin-kpi-cb"
                            label="Circuit breaker (24h)"
                            value={kpi.circuit_breaker_events_24h ?? 0}
                            hint="Users hitting hourly cap"
                            icon={Activity}
                            tone={(kpi.circuit_breaker_events_24h ?? 0) > 5 ? "warn" : "default"}
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Plan distribution */}
                        <section
                            data-testid="admin-plan-distribution"
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                        >
                            <h2 className="text-sm font-semibold uppercase tracking-widest mb-4 text-text-secondary">Plan distribution</h2>
                            <div className="space-y-3">
                                {PLAN_ORDER.map((plan) => {
                                    const count = planMap[plan] || 0;
                                    const total = kpi.users?.total || 1;
                                    const pct = Math.round((count / total) * 100);
                                    return (
                                        <div key={plan} data-testid={`admin-plan-${plan}`}>
                                            <div className="flex justify-between text-xs mb-1">
                                                <span className="uppercase tracking-widest text-text-secondary">{plan}</span>
                                                <span className="tabular-nums">
                                                    {count} <span className="text-text-secondary">({pct}%)</span>
                                                </span>
                                            </div>
                                            <div className="h-2 w-full rounded-full bg-[var(--color-panel-ai)] overflow-hidden">
                                                <div
                                                    className="h-full gradient-bg-cta"
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        {/* Top modules */}
                        <section
                            data-testid="admin-top-modules"
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                        >
                            <h2 className="text-sm font-semibold uppercase tracking-widest mb-4 text-text-secondary">Top modules</h2>
                            {(kpi.top_modules || []).length === 0 ? (
                                <p className="text-sm text-text-secondary">No runs yet.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {kpi.top_modules.map((m, i) => (
                                        <li key={m.slug} className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-[var(--color-panel-ai)]/60 transition-colors">
                                            <span className="flex items-center gap-3">
                                                <span className="text-text-secondary text-xs tabular-nums w-6">#{i + 1}</span>
                                                <Link to={`/modules/${m.slug}`} className="text-sm hover:text-[var(--color-primary)]">{m.name}</Link>
                                            </span>
                                            <span className="text-sm tabular-nums text-text-secondary">{m.runs}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>

                    {/* Recent transactions */}
                    <section
                        data-testid="admin-recent-transactions"
                        className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 mb-6"
                    >
                        <h2 className="text-sm font-semibold uppercase tracking-widest mb-4 text-text-secondary">Recent transactions</h2>
                        {(kpi.recent_transactions || []).length === 0 ? (
                            <p className="text-sm text-text-secondary">No transactions.</p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="text-[10px] uppercase tracking-widest text-text-secondary">
                                    <tr>
                                        <th className="text-left py-2">User</th>
                                        <th className="text-left py-2">Type</th>
                                        <th className="text-right py-2">Amount</th>
                                        <th className="text-left py-2 pl-4">Description</th>
                                        <th className="text-right py-2">When</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {kpi.recent_transactions.map((tx) => (
                                        <tr key={tx.id} className="border-t border-[var(--color-border)]">
                                            <td className="py-2 text-text-secondary text-xs">{tx.user_email}</td>
                                            <td className="py-2 text-xs">{tx.type}</td>
                                            <td className="py-2 text-right tabular-nums">
                                                <span className={tx.amount > 0 ? "text-[var(--color-success)]" : "text-[var(--color-alert)]"}>
                                                    {tx.amount > 0 ? "+" : ""}{tx.amount}
                                                </span>
                                            </td>
                                            <td className="py-2 pl-4 text-xs text-text-secondary truncate max-w-md">
                                                {tx.description}
                                            </td>
                                            <td className="py-2 text-right text-[10px] text-text-secondary">
                                                {tx.created_at?.slice(0, 16).replace("T", " ")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>

                    {/* Quick links */}
                    <section className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                            { to: "/admin/blog", label: "Blog CMS", icon: Rss },
                            { to: "/admin/modules", label: "Modules", icon: Boxes },
                            { to: "/admin/marketplace", label: "Marketplace", icon: ShoppingBag },
                            { to: "/admin/economy", label: "Economy", icon: Activity },
                            { to: "/admin/enterprise-leads", label: "Enterprise leads", icon: Building2 },
                            { to: "/admin/affiliate-payouts", label: "Affiliate payouts", icon: Award },
                            { to: "/admin/insights", label: "Insights", icon: Microscope },
                            { to: "/admin/outreach", label: "Outreach", icon: Send },
                        ].map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`admin-quicklink-${l.to.replace("/admin/", "")}`}
                                className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm text-text-secondary hover:text-text-main hover:border-[var(--color-primary)]/50 transition-colors"
                            >
                                <l.icon className="h-4 w-4" />
                                {l.label}
                            </Link>
                        ))}
                    </section>
                </main>
            </div>
        </div>
    );
}
